import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import Subnav from 'ext-ui/components/Subnav/Subnav'
import TextAreaInput from 'ext-ui/components/TextAreaInput/TextAreaInput'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import {
  CountryCode,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'libphonenumber-js'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import CountryPhoneDropdown from 'src/components/CountryPhoneDropdown/CountryPhoneDropdown'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import PhoneTextInputField from 'src/components/PhoneTextInputField/PhoneTextInputField'
import MtAccountCollection from 'src/feature/MtAccountCollection/MtAccountCollection'
import { useTranslate } from 'src/localization/Trns'
import type { AccountCollectionFlowCompletionResponse } from 'src/queries/payments/payments.types'
import {
  useCompleteVendorAccountCollectionFlow,
  useConfirmVendorAccountCollectionFlow,
  useCreateVendor,
  useCreateVendorAccountCollectionFlow,
  useVendorDetail,
  vendorQueries,
} from 'src/queries/vendors/vendors.queries'
import {
  ConfirmVendorAccountCollectionFlowInput,
  CreateVendorRequest,
  isVendorArchived,
  Vendor,
  VendorAccountCollectionFlowResponse,
  VendorStatus,
} from 'src/queries/vendors/vendors.types'

import PaymentInformationTab, {
  PaymentInformationTabProps,
} from './PaymentInformationTab'
import useVendorAccountConfirmation from './useVendorAccountConfirmation'
import {
  formatPhoneForDisplay,
  useVendorDetailsForm,
} from './useVendorDetailsForm'
import useVendorPaymentMethod from './useVendorPaymentMethod'
import VendorAchAccountCard from './VendorAchAccountCard'
import {
  acknowledgementCopyText,
  VENDOR_ACKNOWLEDGEMENT,
} from './vendorAcknowledgement'
import VendorArchiveAction from './VendorArchiveAction'
import VendorDetailsTab from './VendorDetailsTab'
import style, {
  ackBlock,
  ackDescription,
  ackPrimary,
  ackRow,
  ackText,
  confirmationSurface,
  description,
  fields,
  flyoutTitle,
  footer,
  footerActionsEnd,
  footerButtons,
  footerError,
  form,
  header,
  headerCloseBarAction,
  headerDividerFullBleed,
  headerNoDivider,
  headerTitleRow,
  pendingNote,
  phoneField,
  phoneInput,
  phoneRow,
  tabBarWrap,
  visuallyHidden,
} from './VendorFlyout.style'
import { createVendorIdempotencyKey } from './vendorIdempotency'

export interface VendorFlyoutProps {
  isOpen: boolean
  onClose: () => void
  organizationId: string
  /** Resume an existing, half-finished Vendor instead of adding a new one.
   * The flyout fetches it and opens at whichever step it stopped at: bank
   * account if it has no collection flow, acknowledgement if its flow is
   * AWAITING_ACKNOWLEDGEMENT, and closes immediately if it is archived or
   * already payable. Omitted means "add a new Vendor" — a blank Step 1. */
  resumeVendorId?: string
  /** Open the flyout in EDIT mode for an existing vendor (EX-40771): the
   * tabbed Vendor Details / Payment Information view. Active vendors are
   * editable; Archived vendors are read-only. Distinct from `resumeVendorId`
   * so the resume effect below can never fire for an edit — an Active vendor
   * must open, not slam shut. Only one of `resumeVendorId` / `editVendorId`
   * is ever set. */
  editVendorId?: string
  /** Whether details fields may be edited in edit mode. Defaults on for direct
   * consumers; VendorsPage passes the dedicated EX-40771 gate. */
  editEnabled?: boolean
  /** Whether the Archive header action is visible for non-archived vendors. */
  archiveEnabled?: boolean
  className?: string
}

/** Two steps, one flyout instance (prototype's `mvpStep`): the details form,
 * then the ACH payment step. Run 2 ships Step 1 + a Step 2 placeholder. */
type Step = 'details' | 'payment'

type PaymentStepPhase =
  | 'collecting'
  | 'acknowledgement'
  | 'confirming'
  | 'confirmation-pending'
  | 'confirmation-error'

type SubmittedVendorAccountCollectionFlow = Pick<
  VendorAccountCollectionFlowResponse,
  'flowId'
>

// Moved to its own leaf module so `useVendorPaymentMethod` can share it without
// closing an import cycle (this file imports the hook, the hook needs the copy).
// Re-exported here to keep the historical import path stable.
export { VENDOR_ACKNOWLEDGEMENT } from './vendorAcknowledgement'

/**
 * Reuse the app's established country picker + country-scoped phone input
 * pairing. The picker defaults to US but remains independent from the ACH rail:
 * it describes the vendor contact's phone number, not their bank account.
 */
const DEFAULT_PHONE_ISO_COUNTRY = 'US'

/** Names the regions focus is moved into when Step 2 swaps its subtree. */
const FLYOUT_TITLE_ID = 'add-vendor-flyout-title'

/** Anchored format check (the shared EMAIL_REGEX is unanchored and matches on
 * substrings). Presence is gated separately. */
const isValidEmailFormat = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

const trimmedOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/** The two edit-mode subtabs (EX-40771). `payment` is a placeholder shell in
 * this ticket; EX-40772 fills its body. */
type VendorEditTab = 'details' | 'payment'

const VENDOR_EDIT_TAB_ORDER: VendorEditTab[] = ['details', 'payment']

const VENDOR_EDIT_TAB_IDS: Record<
  VendorEditTab,
  { tab: string; panel: string }
> = {
  details: {
    tab: 'vendor-edit-tab-details',
    panel: 'vendor-edit-panel-details',
  },
  payment: {
    tab: 'vendor-edit-tab-payment',
    panel: 'vendor-edit-panel-payment',
  },
}

interface VendorEditLoadedProps {
  vendor: Vendor
  organizationId: string
  onClose: () => void
  editEnabled: boolean
  archiveEnabled: boolean
}

/**
 * The loaded edit view: header (the vendor's own name — designer call on
 * EX-40756, not "Edit Vendor"), the accessible tab bar, the active panel, and
 * the shell-owned footer. Mounted with a `key` on the vendor id so every open
 * gets a fresh form and a reset acknowledgement.
 *
 * The footer is deliberately tab-agnostic: its visibility keys off "is there a
 * pending change" (today only the details tab contributes), so EX-40772 can add
 * the payment half by contributing to the same footer state rather than forking
 * it. A read-only (Archived) vendor never renders it.
 */
const VendorEditLoaded: React.FC<VendorEditLoadedProps> = ({
  vendor,
  organizationId,
  onClose,
  editEnabled,
  archiveEnabled,
}) => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const isArchived = vendor.status === VendorStatus.Archived
  const isActionRequired = vendor.status === VendorStatus.ActionRequired
  const [activeTab, setActiveTab] = useState<VendorEditTab>(
    isActionRequired ? 'payment' : 'details',
  )

  const form = useVendorDetailsForm({
    vendor,
    organizationId,
    onSaved: (saved) => {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <span>
              <strong>{translate('Details saved')}</strong>{' '}
              {translate("${vendorName}'s vendor details were saved.", {
                vendorName: saved.name,
              })}
            </span>
          ),
        }),
      )
      onClose()
    },
  })

  // EX-40772: the Payment Information tab's data + retire/re-add orchestration.
  // Lives here (not inside the tab) so the SINGLE shell-owned footer below can
  // render the ACH acknowledgement + Confirm and Add — the tab never owns a
  // footer of its own. Reuses the collection-flow lifecycle, no second copy.
  const payment = useVendorPaymentMethod({ organizationId, vendor })

  // Tab renders a plain <div> with no role/tabIndex/keyboard handling of its own
  // (ext-ui DS gap, flagged in the PR), so ARIA + keyboard operation are added
  // here: role="tab", aria-selected/-controls, a ROVING tabindex (only the
  // active tab is in the tab order), and Enter/Space + arrow/Home/End
  // activation. Roving matches the repo's other tablist —
  // component-layout/DetailTabs/useDetailTabs.ts — so the tab bar is one Tab
  // stop and arrows move within it, rather than every tab being its own stop.
  // Focus follows selection below, which is what makes the arrow keys usable.
  const handleTabKeyDown = (
    event: React.KeyboardEvent,
    key: VendorEditTab,
    index: number,
  ): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setActiveTab(key)
      return
    }
    let nextIndex: number | null = null
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % VENDOR_EDIT_TAB_ORDER.length
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex =
        (index - 1 + VENDOR_EDIT_TAB_ORDER.length) %
        VENDOR_EDIT_TAB_ORDER.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = VENDOR_EDIT_TAB_ORDER.length - 1
    }
    if (nextIndex !== null) {
      event.preventDefault()
      const nextKey = VENDOR_EDIT_TAB_ORDER[nextIndex]
      setActiveTab(nextKey)
      document.getElementById(VENDOR_EDIT_TAB_IDS[nextKey].tab)?.focus()
    }
  }

  const tabDescriptors: {
    key: VendorEditTab
    label: string
    severe: boolean
  }[] = [
    { key: 'details', label: translate('Vendor Details'), severe: false },
    {
      key: 'payment',
      label: translate('Payment Information'),
      severe: isActionRequired,
    },
  ]

  // Read-only is BOTH conditions (EX-40761 added the `editEnabled` gate): an
  // archived vendor and a viewer without edit rights are equally look-but-
  // don't-touch, and both tabs have to honour it, not just the details one.
  const readOnly = isArchived || !editEnabled
  // Same gate, but the payment tab needs the REASON: its empty state has
  // different copy for an archived vendor than for a viewer who cannot edit.
  const paymentReadOnly: PaymentInformationTabProps['readOnly'] = isArchived
    ? 'archived'
    : editEnabled
      ? false
      : 'view-only'

  // One shell-owned footer, its content swapped by the active tab: the details
  // acknowledgement + Save Details when editing details, the ACH acknowledgement
  // + Confirm and Add when the Payment Information re-add is awaiting
  // acknowledgement. Never two footers, and never a payment-only disabled check
  // leaking into the details path. (Deviation from the prototype's single button
  // ANDing both acknowledgements: Save Details and Confirm and Add are distinct
  // server operations, so the one footer swaps its whole content by tab.)
  const detailsFooterVisible = activeTab === 'details' && form.isDirty
  const paymentFooterVisible = activeTab === 'payment' && payment.showFooter
  const footerVisible =
    !readOnly && (detailsFooterVisible || paymentFooterVisible)

  return (
    <>
      {/* Sits on the close bar's line, opposite the ×, rather than beside the
          vendor name — see `headerCloseBarAction`. Rendered before the header
          so its DOM order matches its visual order for a screen reader. */}
      {archiveEnabled && !isArchived && (
        <div css={headerCloseBarAction}>
          <VendorArchiveAction
            vendor={vendor}
            organizationId={organizationId}
            onClose={onClose}
          />
        </div>
      )}

      <div css={[header, headerNoDivider]}>
        <h2 css={flyoutTitle} id={FLYOUT_TITLE_ID}>
          {vendor.name}
        </h2>
      </div>

      <div css={tabBarWrap}>
        <Subnav
          role={'tablist'}
          aria-label={translate('Vendor sections')}
          tabs={tabDescriptors.map((descriptor, index) => ({
            children: (
              <span>
                {descriptor.label}
                {descriptor.severe && (
                  <span css={visuallyHidden}>
                    {translate('Action required')}
                  </span>
                )}
              </span>
            ),
            active: activeTab === descriptor.key,
            variant: descriptor.severe ? 'severe' : 'default',
            role: 'tab',
            id: VENDOR_EDIT_TAB_IDS[descriptor.key].tab,
            'aria-selected': activeTab === descriptor.key,
            'aria-controls': VENDOR_EDIT_TAB_IDS[descriptor.key].panel,
            tabIndex: activeTab === descriptor.key ? 0 : -1,
            onClick: () => setActiveTab(descriptor.key),
            onKeyDown: (event: React.KeyboardEvent) =>
              handleTabKeyDown(event, descriptor.key, index),
          }))}
        />
      </div>

      {activeTab === 'details' ? (
        <VendorDetailsTab
          id={VENDOR_EDIT_TAB_IDS.details.panel}
          labelledBy={VENDOR_EDIT_TAB_IDS.details.tab}
          values={form.values}
          fieldError={form.fieldError}
          onTextFieldChange={form.setTextField}
          onPhoneChange={form.setPhone}
          onPhoneCountryChange={form.setPhoneIsoCountry}
          readOnly={readOnly}
          phoneDisplayValue={formatPhoneForDisplay(vendor.phone)}
        />
      ) : (
        <PaymentInformationTab
          id={VENDOR_EDIT_TAB_IDS.payment.panel}
          labelledBy={VENDOR_EDIT_TAB_IDS.payment.tab}
          readOnly={paymentReadOnly}
          payment={payment}
        />
      )}

      {footerVisible && (
        <div css={footer} data-testid={'vendor-edit-footer'}>
          {activeTab === 'details' ? (
            <>
              {form.topLevelError && (
                <p css={footerError} role={'alert'}>
                  {form.topLevelError}
                </p>
              )}
              <div css={ackBlock}>
                <div css={ackRow}>
                  <Checkbox
                    id={'vendor-details-ack'}
                    checked={form.acknowledged}
                    onChange={(event) =>
                      form.setAcknowledged(event.target.checked)
                    }
                    // The visible copy is split across two lines, so the checkbox
                    // states it as one sentence — same shape as the ACH
                    // acknowledgement below. Without the "I acknowledge" lead, a
                    // screen reader announces a bare claim rather than a consent.
                    aria-label={translate(
                      'I acknowledge these vendor details are accurate and up to date.',
                    )}
                    data-testid={'vendor-details-ack-checkbox'}
                  />
                  <label css={ackText} htmlFor={'vendor-details-ack'}>
                    <span css={ackPrimary}>
                      {translate('I acknowledge that...')}
                    </span>
                    <span css={ackDescription}>
                      {translate(
                        'These vendor details are accurate and up to date.',
                      )}
                    </span>
                  </label>
                </div>
                {form.acknowledgementError && (
                  <p css={footerError} role={'alert'}>
                    {form.acknowledgementError}
                  </p>
                )}
                <div css={footerButtons}>
                  <Button
                    variant={'no-style'}
                    type={'button'}
                    onClick={onClose}
                    data-testid={'vendor-details-cancel'}
                  >
                    {translate('Cancel')}
                  </Button>
                  <Button
                    variant={'primary'}
                    type={'button'}
                    disabled={!form.canSave}
                    loading={form.isSaving}
                    onClick={form.submit}
                    data-testid={'vendor-details-save'}
                  >
                    {translate('Save Details')}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            // EX-40772: the Payment Information half of the shared footer — the
            // ACH acknowledgement + Confirm and Add, driven by the same reused
            // confirmation hook the add-vendor flow uses.
            <div css={ackBlock}>
              <div css={ackRow}>
                <Checkbox
                  id={'vendor-payment-ack'}
                  checked={payment.acknowledged}
                  // Also gated on the details being ON SCREEN (EX-40844): this
                  // step exists so the user attests to something they can see.
                  disabled={
                    payment.phase !== 'acknowledgement' ||
                    !payment.canAcknowledgeDetails
                  }
                  onChange={(event) =>
                    payment.setAcknowledged(event.target.checked)
                  }
                  aria-label={translate(
                    'I acknowledge these ACH account details are accurate and authorized for use when paying this vendor.',
                  )}
                  data-testid={'vendor-payment-ack-checkbox'}
                />
                <label css={ackText} htmlFor={'vendor-payment-ack'}>
                  <span css={ackPrimary}>
                    {translate('I acknowledge that...')}
                  </span>
                  <span css={ackDescription}>
                    {translate(
                      'These ACH account details are accurate and authorized for use when paying this vendor.',
                    )}
                  </span>
                </label>
              </div>
              <div css={footerActionsEnd}>
                {payment.confirmation.canRetry ? (
                  <Button
                    variant={'secondary'}
                    type={'button'}
                    onClick={payment.confirmation.retry}
                    data-testid={'vendor-payment-ack-retry'}
                  >
                    {translate('Try again')}
                  </Button>
                ) : payment.confirmation.errorKind !== 'stale-or-terminal' ? (
                  <Button
                    variant={'primary'}
                    type={'button'}
                    disabled={
                      payment.phase !== 'acknowledgement' ||
                      !payment.acknowledged ||
                      !payment.canAcknowledgeDetails ||
                      payment.confirmation.isInFlight
                    }
                    loading={payment.confirmation.isInFlight}
                    onClick={payment.handleConfirmAndAdd}
                    data-testid={'vendor-payment-ack-confirm'}
                  >
                    {/* SPIKE: the same button submits an add and a replace, but
                        they are not the same action to the user — one starts
                        paying a vendor, the other moves where an already-payable
                        vendor gets paid. The label says which. */}
                    {payment.isReplacing
                      ? translate('Confirm and Replace')
                      : translate('Confirm and Add')}
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

interface VendorEditViewProps {
  isOpen: boolean
  organizationId: string
  vendorId: string
  onClose: () => void
  editEnabled: boolean
  archiveEnabled: boolean
}

/**
 * Edit-mode entry (EX-40771): fetches the vendor, shows loading / error chrome,
 * and hands off to {@link VendorEditLoaded} once data is in. The form hooks live
 * in the loaded child so they only run in edit mode — add-mode consumers of this
 * flyout never mount the update mutation.
 */
const VendorEditView: React.FC<VendorEditViewProps> = ({
  isOpen,
  organizationId,
  vendorId,
  onClose,
  editEnabled,
  archiveEnabled,
}) => {
  const translate = useTranslate()
  // Deliberately NOT gated on `isOpen`. BaseFlyout's react-modal stays mounted
  // through its exit transition, so dropping the query key on close would blank
  // the data and flash the loading chrome over a panel still on screen. The
  // shell decides when this view is mounted at all (it latches the edit branch
  // for the duration of the close); while it is mounted, it always has its
  // vendor.
  const vendorQuery = useVendorDetail(organizationId, vendorId)
  const vendor = vendorQuery.data

  if (!vendor) {
    return (
      <>
        {/* Loading is the one no-tabs header that carries no line: it is
            transient, and drawing a rule that the tab bar immediately replaces
            reads as a flicker. A failed query is terminal — no tab bar will
            ever mount — so it falls back to the full-bleed divider. */}
        <div
          css={[
            header,
            vendorQuery.isError ? headerDividerFullBleed : headerNoDivider,
          ]}
        >
          <h2 css={flyoutTitle} id={FLYOUT_TITLE_ID}>
            {translate('Vendor details')}
          </h2>
        </div>
        <div css={fields}>
          {vendorQuery.isError ? (
            <p css={footerError} role={'alert'}>
              {translate('We could not load this vendor. Please try again.')}
            </p>
          ) : (
            <p css={pendingNote} role={'status'} aria-live={'polite'}>
              {translate('Loading vendor details…')}
            </p>
          )}
        </div>
      </>
    )
  }

  return (
    <VendorEditLoaded
      key={vendor.id}
      vendor={vendor}
      organizationId={organizationId}
      onClose={onClose}
      editEnabled={editEnabled}
      archiveEnabled={archiveEnabled}
    />
  )
}

/**
 * Add-Vendor flyout (Bill Pay M2). Routing-free: it owns no URL state — the
 * page owns open/close. Step 1 creates the vendor's Modern Treasury
 * Counterparty (`useCreateVendor`) and advances to Step 2; run 3 replaces the
 * Step 2 placeholder with the real ACH account-collection embed.
 *
 * EX-40771 makes it mode-aware: with `editVendorId` set it renders the tabbed
 * Vendor Details / Payment Information edit view instead of the add/resume
 * flow. Add and resume are unchanged.
 *
 * Chassis: BaseFlyout (focus trap / Escape / overlay dismiss / focus
 * restoration to the triggering CTA are provided by its react-modal) + an
 * in-`children` header/footer, because BaseFlyout has no footer or stepper slot
 * — the AssignAccountOwnerFlyout pattern (DS gap flagged in the PR).
 */
const VendorFlyout: React.FC<VendorFlyoutProps> = ({
  isOpen,
  onClose,
  organizationId,
  resumeVendorId,
  editVendorId,
  editEnabled = true,
  archiveEnabled = false,
  className,
}) => {
  const translate = useTranslate()

  // EDIT mode is a separate, tab-based view (EX-40771). It owns its own data
  // fetch, form state, and footer in VendorEditView below; the add/resume
  // machinery in this component stays inert for it (resumeVendorId is undefined
  // in edit mode, so every add/resume effect no-ops).
  // On close the page clears its flyout target, so `editVendorId` goes undefined
  // in the SAME render that flips `isOpen` — while BaseFlyout's react-modal
  // still has the panel on screen for its exit transition. Reading the raw prop
  // there drops us into the add-mode branch, and the flyout slides away showing
  // "Add Vendor" over an empty form. Hold the last edited vendor for the
  // duration of the close; the next open overwrites it, so add mode still opens
  // blank.
  const closingEditVendorIdRef = useRef<string | undefined>(undefined)
  if (isOpen) {
    closingEditVendorIdRef.current = editVendorId
  }
  const activeEditVendorId = isOpen
    ? editVendorId
    : closingEditVendorIdRef.current
  const isEditMode = activeEditVendorId !== undefined

  const [step, setStep] = useState<Step>(resumeVendorId ? 'payment' : 'details')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [externalId, setExternalId] = useState('')
  const [notes, setNotes] = useState('')
  const [contactName, setContactName] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneIsoCountry, setPhoneIsoCountry] = useState(
    DEFAULT_PHONE_ISO_COUNTRY,
  )
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [nameErrorArmed, setNameErrorArmed] = useState(false)
  const [emailErrorKind, setEmailErrorKind] = useState<
    'required' | 'invalid' | null
  >(null)
  const [phoneErrorArmed, setPhoneErrorArmed] = useState(false)

  // Step 2 state.
  const [createdVendor, setCreatedVendor] = useState<Vendor | null>(null)
  const [submittedFlow, setSubmittedFlow] =
    useState<SubmittedVendorAccountCollectionFlow | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)
  // Set once confirmation succeeds, to hold the payment step visually still
  // while the flyout animates away (see the onConfirmed handler below).
  // Cleared with the rest of the view state, off-screen.
  const [confirmationSucceeded, setConfirmationSucceeded] = useState(false)
  const createIdempotencyRef = useRef<{
    fingerprint: string
    key: string
  } | null>(null)
  const activeFlowRef = useRef<VendorAccountCollectionFlowResponse | null>(null)
  const completionStatusRef = useRef<
    'PENDING' | 'AWAITING_ACKNOWLEDGEMENT' | 'CONFIRMED' | null
  >(null)
  const recoveryAttemptedFlowIdRef = useRef<string | null>(null)
  const isOpenRef = useRef(isOpen)
  isOpenRef.current = isOpen
  // Phase A <-> Phase B replaces the focused subtree (the MT embed unmounts),
  // which would drop focus to <body>. Move it to the region that appears
  // instead. tabIndex={-1}: programmatically focusable, not in the tab order.
  const confirmationRegionRef = useRef<HTMLDivElement | null>(null)
  const collectRegionRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useDispatch()

  // The reset is split in two: the refs run on close, the view state on the
  // next open (see both effects below).
  //
  // REFS = the correctness half. Nulling createIdempotencyRef is what makes
  // "closing abandons the create" true — createVendor's onSuccess compares
  // the key it was called with against this ref and bails on a mismatch.
  const clearVendorSessionRefs = useCallback((): void => {
    createIdempotencyRef.current = null
    activeFlowRef.current = null
    completionStatusRef.current = null
    recoveryAttemptedFlowIdRef.current = null
  }, [])

  // VIEW = everything the user can see, so it must NOT run while the flyout
  // is still on screen — rewinding it mid-exit is exactly the flash this
  // avoids.
  const clearVendorSessionView = useCallback((): void => {
    setStep('details')
    setName('')
    setEmail('')
    setExternalId('')
    setNotes('')
    setContactName('')
    setPhone('')
    setPhoneIsoCountry(DEFAULT_PHONE_ISO_COUNTRY)
    setSubmitError(null)
    setNameErrorArmed(false)
    setEmailErrorKind(null)
    setPhoneErrorArmed(false)
    setCreatedVendor(null)
    setSubmittedFlow(null)
    setAcknowledged(false)
    setConfirmationSucceeded(false)
  }, [])

  const createVendor = useCreateVendor(organizationId, {
    // Same flyout instance advances to Step 2 (the new vendor is now in the
    // list as ACTION_REQUIRED via the hook's list invalidation). Keep the vendor
    // so Step 2 can bind its account-collection flow to the id.
    onSuccess: (vendor, variables) => {
      if (createIdempotencyRef.current?.key !== variables.idempotencyKey) {
        return
      }
      createIdempotencyRef.current = null
      setCreatedVendor(vendor)
      setStep('payment')
    },
  })

  const createFlowMutation = useCreateVendorAccountCollectionFlow()
  const completeFlowMutation =
    useCompleteVendorAccountCollectionFlow(organizationId)
  const confirmFlowMutation =
    useConfirmVendorAccountCollectionFlow(organizationId)
  const recoveryVendorQuery = useVendorDetail(
    organizationId,
    isOpen && !createdVendor ? resumeVendorId : undefined,
  )
  const pendingDetailsQuery = useQuery({
    ...vendorQueries.pendingAccountDetails(
      organizationId,
      createdVendor?.id,
      submittedFlow?.flowId,
    ),
    // `isOpen` so closing and reopening the flyout re-runs a failed read. The
    // card's copy says "try again later" and offers no retry control, and the
    // app client sets `retryOnMount: false`, so without this the error is
    // terminal for the life of the page — reopening would show the same stale
    // failure and never re-request. Matches how `recoveryVendorQuery` above is
    // gated.
    //
    // `!confirmationSucceeded` stops it once confirm lands: the flow flips to
    // COMPLETED server-side and a refetch would 409 into a misleading error
    // card while the flyout animates away (the panel stays mounted through the
    // close transition, so the observer is still live).
    enabled: Boolean(
      isOpen &&
        organizationId &&
        createdVendor?.id &&
        submittedFlow?.flowId &&
        !confirmationSucceeded,
    ),
  })
  // Gate the attestation on details actually being on screen (EX-40844). The
  // resume path is why this is not optional: reopening an Action Required
  // vendor lands straight on this step, possibly for someone who never typed
  // these details and has no other way to know them.
  const canAcknowledgeDetails = pendingDetailsQuery.data != null
  const confirmation = useVendorAccountConfirmation({
    confirmFlow: confirmFlowMutation.mutateAsync,
    onConfirmed: () => {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <span>
              <strong>{translate('Vendor added')}</strong>{' '}
              {translate(
                '${vendorName} is now active with ACH payment details on file.',
                { vendorName: createdVendor?.name ?? '' },
              )}
            </span>
          ),
        }),
      )
      // Latch before closing: on success the hook drops to phase 'idle',
      // which would recompute this step to 'acknowledgement' and visibly
      // re-enable the checkbox and CTA while the flyout is already sliding
      // out. This freezes the panel as it looked when the button was clicked.
      setConfirmationSucceeded(true)
      onClose()
    },
  })
  const resetConfirmation = confirmation.reset
  const abortConfirmation = confirmation.abort
  const paymentStepPhase: PaymentStepPhase = confirmationSucceeded
    ? 'confirming'
    : confirmation.phase === 'error'
      ? 'confirmation-error'
      : confirmation.phase === 'confirming'
        ? 'confirming'
        : confirmation.phase === 'pending'
          ? 'confirmation-pending'
          : submittedFlow
            ? 'acknowledgement'
            : 'collecting'

  // The view state resets on OPEN, not on close. The flyout stays mounted and
  // visible for its whole exit transition (BaseFlyout -> react-modal
  // `closeTimeoutMS`), so resetting on close rewinds the panel to an empty
  // Step 1 and flashes it on the way out. Resetting here gives the same
  // guarantee — a reopened flyout never shows the abandoned session — without
  // depending on the transition's duration.
  //
  // useLayoutEffect, declared HERE, both on purpose: React runs every layout
  // effect before any passive one, so a plain useEffect would land after the
  // resume sync below (itself a layout effect) and clobber its
  // `setStep('payment')` back to Step 1. Same effect type + earlier
  // declaration means this wipes the slate first and the resume sync still
  // gets the last word.
  useLayoutEffect(() => {
    if (!isOpen) return
    resetConfirmation()
    clearVendorSessionView()
  }, [isOpen, resetConfirmation, clearVendorSessionView])

  const handleCollectionSubmitted = useCallback(
    (flow: SubmittedVendorAccountCollectionFlow): void => {
      setAcknowledged(false)
      resetConfirmation()
      setSubmittedFlow(flow)
    },
    [resetConfirmation],
  )
  const createFlowForEmbed = useCallback(async () => {
    const flow = await createFlowMutation.mutateAsync(createdVendor?.id ?? '')
    activeFlowRef.current = flow
    return flow
  }, [createFlowMutation, createdVendor])
  const completeFlowForEmbed = useCallback(
    async (
      flowId: string,
    ): Promise<AccountCollectionFlowCompletionResponse> => {
      const result = await completeFlowMutation.mutateAsync({
        vendorId: createdVendor?.id ?? '',
        flowId,
      })
      completionStatusRef.current = result.status
      return {
        status: result.status === 'PENDING' ? 'PENDING' : 'COMPLETED',
      }
    },
    [completeFlowMutation, createdVendor],
  )
  const handleCollectionLinked = useCallback((): void => {
    if (completionStatusRef.current === 'CONFIRMED') {
      onClose()
      return
    }
    const flow = activeFlowRef.current
    if (flow) handleCollectionSubmitted(flow)
  }, [handleCollectionSubmitted, onClose])

  useEffect(() => {
    // EX-40771: the "Active / Archived / PAYABLE -> onClose()" branch below is
    // the exact set of cases edit mode must OPEN, not close. Edit mode never
    // sets resumeVendorId (so recoveryVendorQuery is disabled and .data is
    // undefined), but guard explicitly too so a future refactor can't route an
    // editable vendor through this close path.
    if (isEditMode) return
    if (!isOpen || createdVendor || !recoveryVendorQuery.data) return
    const vendor = recoveryVendorQuery.data
    setStep('payment')

    if (
      isVendorArchived(vendor) ||
      vendor.status === VendorStatus.Active ||
      vendor.paymentMethod != null
    ) {
      onClose()
      return
    }

    const flow = vendor.accountCollectionFlow
    if (!flow) {
      setCreatedVendor(vendor)
      setSubmittedFlow(null)
      return
    }
    if (flow.status === 'AWAITING_ACKNOWLEDGEMENT') {
      setCreatedVendor(vendor)
      handleCollectionSubmitted(flow)
      return
    }
    if (recoveryAttemptedFlowIdRef.current === flow.flowId) return
    recoveryAttemptedFlowIdRef.current = flow.flowId
    void completeFlowMutation
      .mutateAsync({
        vendorId: vendor.id,
        flowId: flow.flowId,
      })
      .then((result) => {
        if (!isOpenRef.current) return
        if (result.status === 'CONFIRMED') {
          onClose()
          return
        }
        setCreatedVendor(vendor)
        if (result.status === 'AWAITING_ACKNOWLEDGEMENT') {
          handleCollectionSubmitted(flow)
        } else {
          setSubmittedFlow(null)
        }
      })
      .catch(() => {
        if (!isOpenRef.current) return
        setCreatedVendor(vendor)
        setSubmittedFlow(null)
      })
  }, [
    completeFlowMutation,
    createdVendor,
    handleCollectionSubmitted,
    isEditMode,
    isOpen,
    onClose,
    organizationId,
    recoveryVendorQuery.data,
  ])

  useEffect(() => {
    if (step !== 'payment' || !isOpen) return
    if (paymentStepPhase === 'collecting') collectRegionRef.current?.focus()
    else confirmationRegionRef.current?.focus()
  }, [paymentStepPhase, step, isOpen])

  // VendorsPage mounts this flyout once, closed, so the `step` initializer
  // above only ever sees resumeVendorId === undefined. A later row action sets
  // the prop on the already-mounted instance, so the step has to be synced when
  // it opens — otherwise the blank Step 1 form stays on screen for the whole
  // vendor-detail fetch and can be typed into or submitted. useLayoutEffect so
  // the correct step is committed before paint rather than one frame later.
  useLayoutEffect(() => {
    if (!isOpen || !resumeVendorId || createdVendor) return
    setStep('payment')
  }, [isOpen, resumeVendorId, createdVendor])

  // Closing abandons the flow. "Add vendor" always opens a blank Step 1 — it
  // never resumes whatever was in progress last time, because a nav item or a
  // CTA should do the thing it says. This discards only the in-flyout
  // session: a Vendor that Step 1 already persisted still exists, and stays
  // visible in the table as Action Required, which is where resuming belongs
  // rather than in state the user cannot see or dismiss.
  //
  // Only the non-visual half runs here, and it has to be immediate:
  // clearVendorSessionRefs is what makes createVendor's onSuccess bail on a
  // create that lands after close, and abortConfirmation cancels a scheduled
  // confirmation retry. Without the latter, closing during a PENDING confirm
  // would leave the 1s/2s/4s retry chain firing at a closed flyout — and a
  // late CONFIRMED would pop the success toast seconds after the user walked
  // away. `abort` rather than `reset` because reset clears React state, which
  // is exactly what must NOT change while the panel is still animating out.
  //
  // The visible half waits for the next open (see the reset above).
  useEffect(() => {
    if (isOpen) return
    clearVendorSessionRefs()
    abortConfirmation()
  }, [isOpen, clearVendorSessionRefs, abortConfirmation])

  const emailInvalid = email.trim() !== '' && !isValidEmailFormat(email)
  const phoneInvalid =
    phone.trim() !== '' &&
    !isValidPhoneNumber(phone, phoneIsoCountry as CountryCode)

  // canSave === the prototype's `canSave`: Vendor Name present, Vendor Email
  // present AND format-valid, phone valid-if-non-empty. Vendor ID / Mailing
  // Address / Contact Name / Description are all optional — Vendor ID is
  // explicitly NOT required (regression guard: it used to be).
  const canSave =
    name.trim() !== '' &&
    email.trim() !== '' &&
    isValidEmailFormat(email) &&
    !phoneInvalid

  const handleConfirmAndAdd = useCallback((): void => {
    const vendorId = createdVendor?.id
    const flowId = submittedFlow?.flowId
    if (
      !vendorId ||
      !flowId ||
      !acknowledged ||
      paymentStepPhase !== 'acknowledgement' ||
      confirmation.isInFlight
    ) {
      return
    }
    const input: ConfirmVendorAccountCollectionFlowInput = {
      vendorId,
      flowId,
      acknowledged: true,
      acknowledgementCopyVersion: VENDOR_ACKNOWLEDGEMENT.version,
      acknowledgementCopyText: acknowledgementCopyText(
        translate,
        VENDOR_ACKNOWLEDGEMENT,
      ),
    }
    confirmation.startConfirmation(input)
  }, [
    createdVendor,
    submittedFlow,
    acknowledged,
    paymentStepPhase,
    confirmation.isInFlight,
    confirmation.startConfirmation,
    translate,
  ])

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    setSubmitError(null)
    if (name.trim() === '') setNameErrorArmed(true)
    if (email.trim() === '') setEmailErrorKind('required')
    else if (emailInvalid) setEmailErrorKind('invalid')
    if (phoneInvalid) setPhoneErrorArmed(true)
    if (!canSave || createVendor.isPending) return
    const request: CreateVendorRequest = {
      name: name.trim(),
      email: email.trim(),
      contactName: trimmedOrUndefined(contactName),
      phone:
        phone.trim() === ''
          ? undefined
          : `+${getCountryCallingCode(phoneIsoCountry as CountryCode)}${phone.trim()}`,
      externalId: trimmedOrUndefined(externalId),
      notes: trimmedOrUndefined(notes),
    }
    const fingerprint = JSON.stringify(request)
    if (createIdempotencyRef.current?.fingerprint !== fingerprint) {
      createIdempotencyRef.current = {
        fingerprint,
        key: createVendorIdempotencyKey(),
      }
    }
    const idempotencyKey = createIdempotencyRef.current.key
    // Keep the create failure on the mutate callback so a failed save surfaces
    // inline and the user stays on Step 1 with fields intact (the flyout does
    // not unmount on error).
    createVendor.mutate(
      { request, idempotencyKey },
      {
        onError: () => {
          if (
            !isOpenRef.current ||
            createIdempotencyRef.current?.key !== idempotencyKey
          ) {
            return
          }
          setSubmitError(
            translate(
              'Something went wrong adding this vendor. Please try again.',
            ),
          )
        },
      },
    )
  }

  const confirmationErrorMessage =
    confirmation.errorKind === 'stale-or-terminal'
      ? translate(
          'This bank account setup can no longer be confirmed. Close this window and start again.',
        )
      : confirmation.errorKind === 'not-found'
        ? translate(
            'This vendor or bank account setup could not be found. Please try again or close this window and start again.',
          )
        : confirmation.errorKind === 'acknowledgement-required'
          ? translate(
              'We could not record your acknowledgement. Please try again.',
            )
          : confirmation.errorKind === 'provider'
            ? translate(
                'We could not confirm this bank account right now. Please try again.',
              )
            : confirmation.errorKind === 'unexpected'
              ? translate(
                  'We received an unexpected confirmation response. Please try again.',
                )
              : null

  const title =
    step === 'details'
      ? translate('Add Vendor')
      : translate('Add ACH Payment Details')
  const stepDescription =
    step === 'details'
      ? translate('Create a vendor with the details needed to pay them.')
      : translate(
          "Enter this vendor's bank account details below. After submitting, review and confirm your acknowledgement.",
        )

  return (
    <BaseFlyout
      className={cn('VendorFlyout', className)}
      css={style}
      isOpen={isOpen}
      onClose={onClose}
      aria-label={
        isEditMode ? translate('Vendor details') : translate('Add Vendor')
      }
    >
      {isEditMode ? (
        <VendorEditView
          isOpen={isOpen}
          organizationId={organizationId}
          vendorId={activeEditVendorId as string}
          onClose={onClose}
          editEnabled={editEnabled}
          archiveEnabled={archiveEnabled}
        />
      ) : (
        <>
          <div css={[header, headerDividerFullBleed]}>
            <div css={headerTitleRow}>
              <h2 css={flyoutTitle} id={FLYOUT_TITLE_ID}>
                {title}
              </h2>
              {archiveEnabled &&
                resumeVendorId &&
                createdVendor &&
                !isVendorArchived(createdVendor) && (
                  <VendorArchiveAction
                    vendor={createdVendor}
                    organizationId={organizationId}
                    onClose={onClose}
                  />
                )}
            </div>
            <p css={description}>{stepDescription}</p>
          </div>

          {step === 'details' ? (
            <form css={form} onSubmit={handleSubmit} noValidate>
              <div css={fields}>
                <TextInput
                  label={translate('Vendor Name')}
                  requirement={'required'}
                  placeholder={translate('Enter vendor name')}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  errorText={
                    nameErrorArmed && name.trim() === ''
                      ? translate('Vendor name is required')
                      : undefined
                  }
                  testId={'add-vendor-name'}
                />
                <TextInput
                  label={translate('Vendor Email')}
                  requirement={'required'}
                  type={'email'}
                  inputMode={'email'}
                  placeholder={translate('Enter vendor email')}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  errorText={
                    emailErrorKind === 'required' && email.trim() === ''
                      ? translate('Vendor email is required')
                      : emailErrorKind === 'invalid' && emailInvalid
                        ? translate('Enter a valid email address')
                        : undefined
                  }
                  tooltipProps={{
                    children: translate(
                      'Payment remittance details are sent to this address every time you pay this vendor.',
                    ),
                  }}
                  testId={'add-vendor-email'}
                />
                <TextInput
                  label={translate('Vendor ID')}
                  placeholder={translate('Enter vendor ID')}
                  value={externalId}
                  onChange={(event) => setExternalId(event.target.value)}
                  tooltipProps={{
                    children: translate(
                      "Your organization's own identifier for this vendor, like an ID from your accounting system.",
                    ),
                  }}
                  testId={'add-vendor-external-id'}
                />
                {/* No `requirement` prop: optional fields carry no marker at all,
                only required ones get the "*". The label-order wrapper that
                used to sit here existed solely to move "(Optional)" in front
                of the tooltip, so it went with it. */}
                <TextInput
                  label={translate('Contact Name')}
                  placeholder={translate('Enter contact name')}
                  value={contactName}
                  onChange={(event) => setContactName(event.target.value)}
                  tooltipProps={{
                    children: translate(
                      'Your accounts payable contact at this vendor — who to reach for billing questions.',
                    ),
                  }}
                  testId={'add-vendor-contact-name'}
                />
                <div css={phoneField}>
                  <FieldLabel htmlFor={'vendorPhoneNumber'}>
                    {translate('Phone Number')}
                  </FieldLabel>
                  <div css={phoneRow}>
                    <CountryPhoneDropdown
                      id={'vendor'}
                      selectedValue={phoneIsoCountry}
                      chooseOption={setPhoneIsoCountry}
                    />
                    <PhoneTextInputField
                      id={'vendor'}
                      css={phoneInput}
                      countryIso={phoneIsoCountry}
                      value={phone}
                      onChange={setPhone}
                      errors={
                        phoneErrorArmed && phoneInvalid
                          ? [translate('Enter a valid phone number')]
                          : undefined
                      }
                    />
                  </div>
                </div>
                <TextAreaInput
                  aria-label={translate('Vendor Description')}
                  placeholder={translate('Enter vendor description')}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={3}
                  testId={'add-vendor-notes'}
                />
              </div>
              <div css={footer}>
                {submitError && (
                  <p css={footerError} role={'alert'}>
                    {submitError}
                  </p>
                )}
                <div css={footerButtons}>
                  {/* inlineMuted in the prototype -> ext-ui `no-style` variant. */}
                  <Button
                    variant={'no-style'}
                    type={'button'}
                    onClick={onClose}
                    data-testid={'add-vendor-cancel'}
                  >
                    {translate('Cancel')}
                  </Button>
                  <Button
                    variant={'primary'}
                    type={'submit'}
                    disabled={createVendor.isPending}
                    loading={createVendor.isPending}
                    data-testid={'add-vendor-submit'}
                  >
                    {translate('Save and Continue')}
                  </Button>
                </div>
              </div>
            </form>
          ) : !createdVendor ? (
            <div
              css={fields}
              ref={confirmationRegionRef}
              tabIndex={-1}
              role={'group'}
              aria-labelledby={FLYOUT_TITLE_ID}
            >
              <p
                css={recoveryVendorQuery.isError ? footerError : pendingNote}
                role={recoveryVendorQuery.isError ? 'alert' : 'status'}
                aria-live={recoveryVendorQuery.isError ? undefined : 'polite'}
              >
                {recoveryVendorQuery.isError
                  ? translate(
                      'We could not restore this vendor bank account setup. Close this window and try again.',
                    )
                  : translate('Restoring this vendor bank account setup…')}
              </p>
            </div>
          ) : paymentStepPhase === 'collecting' ? (
            // Phase A — collecting. The MT embed sits directly in the flyout
            // body, with no wrapper framing it off. The prototype's dashed
            // outline marked the region as Modern Treasury's to design, not a
            // border to ship; the goal is for the embed to read as Extend, which
            // is also why MtAccountCollection themes the iframe with ext-ui
            // tokens. BankAccountsPage embeds the same flow unframed. No flyout
            // footer either: the embed owns its own Submit.
            <div css={fields} ref={collectRegionRef} tabIndex={-1}>
              <MtAccountCollection
                createFlow={createFlowForEmbed}
                completeFlow={completeFlowForEmbed}
                onLinked={handleCollectionLinked}
                // The flyout header already says "Add ACH Payment Details"
                // and describes this exact step, so the embed's own title
                // and subtitle are duplicate copy here.
                showHeading={false}
                // The flyout's field column already pads horizontally; the
                // component's own 40px on top of it inset the MT form well
                // inside Step 1's own inputs.
                flush
              />
            </div>
          ) : (
            // Phase B — the provider accepted the hosted form. Only the user's
            // later checkbox + explicit button activation can start /confirm.
            <div
              css={confirmationSurface}
              ref={confirmationRegionRef}
              tabIndex={-1}
              role={'group'}
              aria-labelledby={FLYOUT_TITLE_ID}
            >
              <div css={fields}>
                <VendorAchAccountCard
                  details={pendingDetailsQuery.data}
                  isLoading={pendingDetailsQuery.isLoading}
                  isError={pendingDetailsQuery.isError}
                  testId={'vendor-pending-account-card'}
                />
                {paymentStepPhase === 'confirmation-pending' && (
                  <p css={pendingNote} role={'status'} aria-live={'polite'}>
                    {confirmation.canRetry
                      ? translate(
                          'This bank account is still being confirmed. Try again to check its status.',
                        )
                      : translate(
                          'This bank account is still being confirmed. This can take a moment.',
                        )}
                  </p>
                )}
                {paymentStepPhase === 'confirmation-error' &&
                  confirmationErrorMessage && (
                    <p css={footerError} role={'alert'}>
                      {confirmationErrorMessage}
                    </p>
                  )}
                {/* No VISIBLE in-flight note: the embed's submit already created
                the external account at Modern Treasury, so this step is only
                Extend's own acknowledgement and a "confirming this bank
                account" line reads as a second verification step that isn't
                happening. The announcement still has to exist though —
                submitting disables the checkbox and CTA, which drops keyboard
                focus and leaves a screen-reader user with no signal at all
                (WCAG 2.1 AA 4.1.3). So the live region stays, visually
                hidden. The pending/error notes above are visible — those are
                exception paths the user has to act on. */}
                {paymentStepPhase === 'confirming' && (
                  <p css={visuallyHidden} role={'status'} aria-live={'polite'}>
                    {translate('Adding this vendor…')}
                  </p>
                )}
              </div>
              <div css={footer}>
                {submittedFlow ? (
                  <div css={ackBlock}>
                    <div css={ackRow}>
                      <Checkbox
                        id={'vendor-ack'}
                        checked={acknowledged}
                        // Also gated on the details being ON SCREEN (EX-40844).
                        disabled={
                          paymentStepPhase !== 'acknowledgement' ||
                          !canAcknowledgeDetails
                        }
                        onChange={(event) =>
                          setAcknowledged(event.target.checked)
                        }
                        aria-label={translate(
                          'I acknowledge these ACH account details are accurate and authorized for use when paying this vendor.',
                        )}
                        data-testid={'vendor-ack-checkbox'}
                      />
                      <label css={ackText} htmlFor={'vendor-ack'}>
                        <span css={ackPrimary}>
                          {translate('I acknowledge that...')}
                        </span>
                        <span css={ackDescription}>
                          {translate(
                            'These ACH account details are accurate and authorized for use when paying this vendor.',
                          )}
                        </span>
                      </label>
                    </div>
                    <div css={footerActionsEnd}>
                      {confirmation.canRetry ? (
                        <Button
                          variant={'secondary'}
                          onClick={confirmation.retry}
                        >
                          {translate('Try again')}
                        </Button>
                      ) : confirmation.errorKind !== 'stale-or-terminal' ? (
                        <Button
                          variant={'primary'}
                          disabled={
                            paymentStepPhase !== 'acknowledgement' ||
                            !acknowledged ||
                            !canAcknowledgeDetails ||
                            confirmation.isInFlight
                          }
                          loading={confirmation.isInFlight}
                          onClick={handleConfirmAndAdd}
                          data-testid={'vendor-ack-confirm'}
                        >
                          {translate('Confirm and Add')}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </>
      )}
    </BaseFlyout>
  )
}

export default VendorFlyout
